import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, Order } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { deleteOne } from "./delete-orders";

interface Props {
  order: Order;
  /** The owner's code, given just before. */
  authorization: Authorization;
  /** The special admin code from Tafo, entered behind the small ⚙; sent with the delete. */
  adminCode: string;
  /** Tafo changed or withdrew the code meanwhile: Verwijderen disappears again. */
  onAdminCodeRefused: () => void;
  onDeleted: () => void;
  onClose: () => void;
}

/**
 * The warning before an order is deleted for good (in-app text (b) of the approved terms): only a
 * mistaken order, at most 5 a day, and the shop alone is responsible. [Terug] [Definitief verwijderen].
 * An order in a closed Z asks a reason: its removal is kept on the recomputed Z.
 */
export function DeleteDialog({ order, authorization, adminCode, onAdminCodeRefused, onDeleted, onClose }: Props) {
  const { api, toast } = useTill();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  /** In a closed Z: removing recomputes the Z and asks why. */
  const closedZ = order.zNumber;

  const remove = async () => {
    if (closedZ !== null && reason.trim().length < 5) {
      setError(t.orders.deleteReasonMissing);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deleteOne(api, order, { authorization, adminCode }, reason.trim());
      toast(t.orders.deleted);
      onDeleted();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
      if (failure instanceof ApiError && failure.code.includes("admin_code")) onAdminCodeRefused();
    }
  };

  return (
    <Dialog
      title={t.orders.deleteTitle(order.code)}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose} data-autofocus>
            {t.orders.back}
          </button>
          <button type="button" className="btn danger" disabled={busy} onClick={() => void remove()}>
            {t.orders.deleteConfirm}
          </button>
        </>
      }
    >
      <div className="delbox">
        <p>
          {t.orders.deleteOnly[0]}
          <b>{t.orders.deleteOnly[1]}</b>
          {t.orders.deleteOnly[2]}
        </p>
        <p>
          {t.orders.deleteLimit[0]}
          <b>{t.orders.deleteLimit[1]}</b>
          {t.orders.deleteLimit[2]}
        </p>
        <p className="note">{t.orders.deleteResponsibility}</p>
      </div>
      {closedZ !== null && (
        <div className="delbox after-z">
          <p>
            {t.orders.deleteAfterZ(closedZ)[0]}
            <b>{t.orders.deleteAfterZ(closedZ)[1]}</b>
            {t.orders.deleteAfterZ(closedZ)[2]}
          </p>
          <p>{t.orders.deleteAfterZKept}</p>
          <label>
            {t.orders.deleteReason}
            <input
              value={reason}
              maxLength={200}
              placeholder={t.orders.deleteReasonHint}
              onChange={(event) => {
                setReason(event.target.value);
                setError(null);
              }}
            />
          </label>
        </div>
      )}
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
